import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Grid,
  TextField,
  Button,
  Rating,
  Chip,
  Divider,
  IconButton,
  Badge,
  useTheme,
  useMediaQuery,
  Tab,
  Tabs,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Star as StarIcon,
  Send as SendIcon,
  Reply as ReplyIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  AccessTime as AccessTimeIcon,
  PersonOutline as PersonOutlineIcon,
  FilterAlt as FilterAltIcon,
  Flag as FlagIcon,
  Sort as SortIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Search as SearchIcon,
  Done as DoneIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

// Define reviews data outside the component to avoid initialization issues
const reviewsData = [
    {
      id: 1,
      guestName: "John Doe",
    avatar: "J",
      rating: 5,
    comment: "Great stay! The staff was very friendly and the room was clean and spacious. The location is perfect, just minutes from the city center. The breakfast was delicious with many options. Would definitely recommend and stay here again on my next visit.",
      date: "2023-10-15",
      responded: true,
    response: "Thank you for your wonderful feedback! We're delighted to hear you enjoyed your stay with us. We look forward to welcoming you back soon!",
    responseDate: "2023-10-16",
    },
    {
      id: 2,
      guestName: "Jane Smith",
    avatar: "JS",
      rating: 4,
    comment: "The location was perfect, but the breakfast could be improved. The room was comfortable and clean. Staff was friendly and helpful.",
      date: "2023-10-10",
      responded: false,
    },
    {
      id: 3,
      guestName: "Alice Johnson",
    avatar: "A",
      rating: 3,
    comment: "The room was okay, but the noise from construction nearby was distracting. The staff tried their best to accommodate us, which we appreciate.",
      date: "2023-10-05",
      responded: false,
    },
  {
    id: 4,
    guestName: "Robert Brown",
    avatar: "RB",
    rating: 5,
    comment: "Exceptional service! The staff went above and beyond to make our anniversary special. The room was beautiful with a great view. Will definitely return!",
    date: "2023-09-28",
    responded: true,
    response: "We're thrilled to hear we could make your anniversary special! Thank you for choosing our hotel for your celebration.",
    responseDate: "2023-09-29",
  },
  {
    id: 5,
    guestName: "Emily Wilson",
    avatar: "EW",
    rating: 2,
    comment: "Disappointed with the cleanliness of the room. The bathroom had mold in the corners and the carpet was stained. Not worth the price we paid.",
    date: "2023-09-20",
    responded: false,
    flag: "critical",
  },
];

const Reviews = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  
  const [tabValue, setTabValue] = useState(0);
  const [sortValue, setSortValue] = useState("recent");
  const [filterValue, setFilterValue] = useState("all");
  const [expandedReview, setExpandedReview] = useState(null);
  const [responses, setResponses] = useState({});
  
  // Add new state variables
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [currentReviewId, setCurrentReviewId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [reviews, setReviews] = useState([...reviewsData]); // Use the predefined reviewsData array
  const [loadingReviews, setLoadingReviews] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleResponseChange = (id, value) => {
    setResponses({
      ...responses,
      [id]: value,
    });
  };
  
  const handleOpenConfirmDialog = (id) => {
    setCurrentReviewId(id);
    setConfirmDialogOpen(true);
  };
  
  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleRefreshReviews = () => {
    setLoadingReviews(true);
    // Simulate loading delay
    setTimeout(() => {
      setLoadingReviews(false);
    }, 1000);
  };

  const handleSubmitResponse = () => {
    if (!currentReviewId) return;
    
    setSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      // Update the review in our local data
      const updatedReviews = reviews.map(review => 
        review.id === currentReviewId ? {
          ...review,
          responded: true,
          response: responses[currentReviewId],
          responseDate: new Date().toISOString().slice(0, 10)
        } : review
      );
      
      setReviews(updatedReviews);
      
      // Clear the response field
      setResponses({
        ...responses,
        [currentReviewId]: "",
      });
      
      // Show success message
      setSuccessMessage("Response submitted successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Close dialog and reset states
      setSubmitting(false);
      setConfirmDialogOpen(false);
      setCurrentReviewId(null);
    }, 1500);
  };
  
  const handleExpandReview = (id) => {
    setExpandedReview(expandedReview === id ? null : id);
  };
  
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const getFilteredReviews = () => {
    let filtered = [...reviews];
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(review => 
        review.guestName.toLowerCase().includes(query) || 
        review.comment.toLowerCase().includes(query)
      );
    }
    
    // Apply filters
    if (filterValue === "unresponded") {
      filtered = filtered.filter(review => !review.responded);
    } else if (filterValue === "critical") {
      filtered = filtered.filter(review => review.rating <= 3);
    } else if (filterValue === "positive") {
      filtered = filtered.filter(review => review.rating >= 4);
    }
    
    // Apply sorting
    if (sortValue === "recent") {
      filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    } else if (sortValue === "rating-high") {
      filtered.sort((a, b) => b.rating - a.rating);
    } else if (sortValue === "rating-low") {
      filtered.sort((a, b) => a.rating - b.rating);
    }
    
    return filtered;
  };

  const filteredReviews = getFilteredReviews();
  
  // Calculate stats
  const totalReviews = reviews.length;
  const unrespondedReviews = reviews.filter(r => !r.responded).length;
  const averageRating = (reviews.reduce((acc, r) => acc + r.rating, 0) / totalReviews).toFixed(1);
  
  // Count ratings
  const ratingCounts = [0, 0, 0, 0, 0]; // 1-5 stars
  reviews.forEach(r => {
    ratingCounts[r.rating - 1]++;
  });
  
  const ratingPercentages = ratingCounts.map(count => (count / totalReviews) * 100);
  
  // Animation variants
  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };
  
  const emptyStateAnimation = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary" 
            sx={{ display: "flex", alignItems: "center", mb: 0 }}>
            <ChatBubbleOutlineIcon sx={{ mr: 1 }} /> Guest Reviews
          </Typography>
          
          <Tooltip title="Refresh reviews">
            <IconButton 
              color="primary"
              onClick={handleRefreshReviews}
              disabled={loadingReviews}
            >
              {loadingReviews ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </motion.div>

      <Box sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="All Reviews" />
          <Tab 
            label="Needs Response" 
            icon={<Badge badgeContent={unrespondedReviews} color="error" sx={{ "& .MuiBadge-badge": { top: -8, right: -8 } }} />}
            iconPosition="end"
          />
          <Tab label="Analytics" />
        </Tabs>
      </Box>

      {/* All Reviews Tab */}
      <Box sx={{ display: tabValue === 0 ? "block" : "none" }}>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerAnimation}
        >
          {/* Filters row */}
          <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Filter</InputLabel>
                <Select
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  label="Filter"
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterAltIcon fontSize="small" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="unresponded">Unresponded</MenuItem>
                  <MenuItem value="critical">Critical (≤ 3★)</MenuItem>
                  <MenuItem value="positive">Positive (≥ 4★)</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortValue}
                  onChange={(e) => setSortValue(e.target.value)}
                  label="Sort By"
                  startAdornment={
                    <InputAdornment position="start">
                      <SortIcon fontSize="small" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="recent">Most Recent</MenuItem>
                  <MenuItem value="rating-high">Highest Rating</MenuItem>
                  <MenuItem value="rating-low">Lowest Rating</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <TextField
              placeholder="Search reviews..."
              size="small"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: { xs: 1, sm: 0 }, width: { xs: "100%", sm: "auto" }, mt: { xs: 2, sm: 0 } }}
            />
          </Box>
          
          {/* Reviews list */}
          {filteredReviews.length > 0 ? (
            <Grid container spacing={3}>
              {filteredReviews.map((review) => (
                <Grid item xs={12} key={review.id}>
                  <motion.div variants={itemAnimation} transition={{ duration: 0.3 }}>
                    <Card 
                      elevation={2} 
                      sx={{ 
                        borderLeft: review.responded ? '4px solid transparent' : '4px solid',
                        borderLeftColor: review.flag === "critical" ? 'error.main' : 'warning.main',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      <CardHeader
                        avatar={
                          <Avatar sx={{ bgcolor: review.rating >= 4 ? 'success.main' : 
                                      review.rating === 3 ? 'warning.main' : 'error.main' }}>
                            {review.avatar}
                          </Avatar>
                        }
                        title={
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {review.guestName}
                            </Typography>
                            {!review.responded && (
                              <Chip 
                                size="small" 
                                label="Awaiting Response" 
                                color="warning" 
                                sx={{ height: 20 }}
                              />
                            )}
                            {review.flag === "critical" && (
                              <Tooltip title="Critical review" arrow>
                                <Chip 
                                  size="small" 
                                  icon={<FlagIcon fontSize="small" />} 
                                  label="Priority" 
                                  color="error" 
                                  sx={{ height: 20 }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        subheader={
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary', fontSize: '0.9rem' }} />
                            <Typography variant="body2" color="text.secondary">{review.date}</Typography>
                          </Box>
                        }
                        action={
                          <Rating 
                            value={review.rating} 
                            readOnly 
                            precision={0.5} 
                            icon={<StarIcon fontSize="inherit" />}
                            emptyIcon={<StarIcon fontSize="inherit" />}
                          />
                        }
                      />
                      <CardContent>
                        <Typography variant="body1" paragraph>
                          {review.comment}
                        </Typography>
                        
                        {review.responded && (
                          <Box sx={{ 
                            mt: 2,
                            p: 2, 
                            bgcolor: 'background.default', 
                            borderRadius: 1,
                            borderLeft: 2,
                            borderColor: 'primary.main'
                          }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Your Response - {review.responseDate}
                            </Typography>
                            <Typography variant="body2">
                              {review.response}
                            </Typography>
                          </Box>
                        )}
                        
                        {!review.responded && (
                          <Box sx={{ mt: 2 }}>
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              placeholder="Write your response here..."
                              value={responses[review.id] || ""}
                              onChange={(e) => handleResponseChange(review.id, e.target.value)}
                              variant="outlined"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                                    <ReplyIcon />
                                  </InputAdornment>
                                ),
                              }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                              <Button
                                variant="contained"
                                color="primary"
                                endIcon={<SendIcon />}
                                onClick={() => handleOpenConfirmDialog(review.id)}
                                disabled={!responses[review.id]}
                              >
                                Send Response
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          ) : (
            <motion.div 
              variants={emptyStateAnimation}
              initial="hidden"
              animate="visible"
            >
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 1
                }}
              >
                <ChatBubbleOutlineIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  No reviews found
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {searchQuery 
                    ? "Try adjusting your search or filters"
                    : "No reviews match the selected filters"}
                </Typography>
              </Box>
            </motion.div>
          )}
        </motion.div>
      </Box>

      {/* Needs Response Tab */}
      <Box sx={{ display: tabValue === 1 ? "block" : "none" }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Reviews Requiring Attention
          </Typography>
          
          {reviews.filter(review => !review.responded).length > 0 ? (
            <Grid container spacing={3}>
              {reviews.filter(review => !review.responded).map((review) => (
                <Grid item xs={12} key={review.id}>
                  <motion.div 
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card 
                      elevation={2} 
                      sx={{ 
                        borderLeft: '4px solid',
                        borderLeftColor: review.rating <= 3 ? 'error.main' : 'warning.main',
                      }}
                    >
                      <CardHeader
                        avatar={
                          <Avatar sx={{ bgcolor: review.rating >= 4 ? 'success.main' : 
                                    review.rating === 3 ? 'warning.main' : 'error.main' }}>
                            {review.avatar}
                          </Avatar>
                        }
                        title={review.guestName}
                        subheader={`Submitted on ${review.date}`}
                        action={
                          <Box>
                            <Rating value={review.rating} readOnly size="small" />
                            <Chip 
                              size="small" 
                              label={review.rating <= 3 ? 'Priority' : 'Awaiting Response'} 
                              color={review.rating <= 3 ? 'error' : 'warning'} 
                              sx={{ ml: 1, height: 20 }}
                            />
                          </Box>
                        }
                      />
                      <CardContent>
                        <Typography variant="body2" paragraph>
                          {review.comment}
                        </Typography>
                        
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="Write your response here..."
                          value={responses[review.id] || ""}
                          onChange={(e) => handleResponseChange(review.id, e.target.value)}
                          variant="outlined"
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            endIcon={<SendIcon />}
                            onClick={() => handleOpenConfirmDialog(review.id)}
                            disabled={!responses[review.id]}
                          >
                            Send Response
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          ) : (
            <motion.div 
              variants={emptyStateAnimation}
              initial="hidden"
              animate="visible"
            >
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 1
                }}
              >
                <DoneIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  All caught up!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  You've responded to all guest reviews
                </Typography>
              </Box>
            </motion.div>
          )}
        </motion.div>
      </Box>

      {/* Analytics Tab */}
      <Box sx={{ display: tabValue === 2 ? "block" : "none" }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Grid container spacing={4}>
            {/* Overview Cards */}
            <Grid item xs={12} md={4}>
              <Card elevation={3}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>Overall Rating</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 2 }}>
                    <Typography variant="h2" color="primary" fontWeight="bold">
                      {averageRating}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', ml: 1, mb: 1 }}>
                      <StarIcon color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        /5
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Based on {totalReviews} reviews
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card elevation={3}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>Response Rate</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <Typography variant="h2" color="primary" fontWeight="bold">
                      {Math.round(((totalReviews - unrespondedReviews) / totalReviews) * 100)}%
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {unrespondedReviews} reviews need your response
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card elevation={3}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>Critical Reviews</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <Typography variant="h2" color={ratingCounts[0] + ratingCounts[1] > 0 ? "error" : "primary"} fontWeight="bold">
                      {ratingCounts[0] + ratingCounts[1]}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Reviews with 1-2 star ratings
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Rating Distribution */}
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardHeader title="Rating Distribution" />
                <CardContent>
                  <Box>
                    {[5, 4, 3, 2, 1].map((rating, index) => (
                      <Box key={rating} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ width: 40, display: 'flex', alignItems: 'center' }}>
                          <Typography>{rating}</Typography>
                          <StarIcon fontSize="small" sx={{ ml: 0.5, color: 'primary.main' }} />
                        </Box>
                        <Box sx={{ flexGrow: 1, ml: 2 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={ratingPercentages[5 - rating]} 
                            sx={{ 
                              height: 10, 
                              borderRadius: 5,
                              backgroundColor: theme.palette.grey[200],
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: rating >= 4 ? 'success.main' : 
                                                rating === 3 ? 'warning.main' : 
                                                'error.main',
                                borderRadius: 5,
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2, minWidth: 50 }}>
                          {ratingCounts[5 - rating]} ({Math.round(ratingPercentages[5 - rating])}%)
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </motion.div>
      </Box>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Response</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to submit this response? Once posted, guests will be able to see your reply.
          </DialogContentText>
          <Box sx={{ mt: 2, bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Your response:</Typography>
            <Typography variant="body2">
              {currentReviewId ? responses[currentReviewId] : ''}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitResponse} 
            variant="contained" 
            color="primary"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {submitting ? 'Submitting...' : 'Confirm & Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success Snackbar */}
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={3000} 
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccessMessage(null)} 
          severity="success" 
          variant="filled" 
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reviews;